import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SignOut, House, Folder, FileArrowUp, CurrencyDollar, RocketLaunch, Moon, Sun, FileText, Briefcase, PaperPlaneTilt } from '@phosphor-icons/react';
import WelcomeTourModal from './WelcomeTourModal';
import { BackButton } from './BackButton';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';

export function PortalLayout({ children, activePage }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/portail');
  };

  const navItems = [
    { key: 'dashboard', label: 'Accueil', icon: House, path: '/portail/dashboard' },
    { key: 'onboarding', label: 'Mon Onboarding', icon: RocketLaunch, path: '/portail/onboarding' },
    { key: 'dossiers', label: 'Mes dossiers', icon: Folder, path: '/portail/dossiers' },
    { key: 'missions', label: 'Mes missions', icon: Briefcase, path: '/portail/missions' },
    { key: 'conventions', label: 'Mes conventions', icon: FileText, path: '/portail/conventions' },
    { key: 'publications', label: 'Publications', icon: PaperPlaneTilt, path: '/portail/publications' },
    { key: 'annuaire', label: 'Annuaire coopérateurs', icon: FileText, path: '/portail/annuaire' },
    { key: 'documents', label: 'Documents', icon: FileArrowUp, path: '/portail/documents' },
    { key: 'paiements', label: 'Paiements', icon: CurrencyDollar, path: '/portail/paiements' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F5]">
      <WelcomeTourModal />
      {/* Top bar */}
      <header className="bg-white border-b border-[#E8E6E1] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton fallback="/portail/dashboard" testId="portal-back-btn" />
            <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="w-9 h-9 object-contain" data-testid="portal-header-logo" />
            <div>
              <p className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Portail Membre</p>
              <p className="text-[10px] text-[#5E6D66]">Objectif SCOP Outremer</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors"
              data-testid="portal-theme-toggle"
              title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            >
              {theme === 'dark' ? (
                <Sun size={18} className="text-amber-400" weight="fill" />
              ) : (
                <Moon size={18} className="text-[#5E6D66]" />
              )}
            </button>
            <span className="text-sm text-[#5E6D66] hidden sm:block">{user?.name || user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#5E6D66] hover:text-[#D47A62]" data-testid="portal-logout-btn">
              <SignOut size={18} className="mr-1" /> Deconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation tabs */}
      <nav className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activePage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                data-testid={`portal-nav-${item.key}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-[#2D5A4C] text-[#2D5A4C]'
                    : 'border-transparent text-[#5E6D66] hover:text-[#1A2421] hover:border-[#E8E6E1]'
                }`}
              >
                <Icon size={18} />
                {item.label}
              </button>
            );
          })}
        </div>
        {/* Colored accent bar */}
        <div className="flex max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex gap-0.5 w-full">
            <div className="h-[2px] flex-1" style={{ background: '#00AEEF' }} />
            <div className="h-[2px] flex-1" style={{ background: '#80C51F' }} />
            <div className="h-[2px] flex-1" style={{ background: '#EA3C3A' }} />
            <div className="h-[2px] flex-1" style={{ background: '#FF8E00' }} />
            <div className="h-[2px] flex-1" style={{ background: '#D5007D' }} />
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
